import moment from 'moment';
import { FilterStatusEnum } from '../../utils/Enum';

export const candidateSortOptions = {
  previouslyRated: 'previously-rated',
  skillsMatch: 'skills-match',
  localFavorites: 'local-favorites',
};

export const defaultFilters = {
  sort: candidateSortOptions.localFavorites,
  page: 1,
  totalPages: 1,
  limit: 5,
};

/**
 * How often the Confirm Candidate screen re-reads its candidate list while open, and the
 * window a cached read stays fresh (GOTU-8310). The interval tick fetches regardless of
 * staleness; `staleTime` only gates the other triggers (remount, tab return). One value for
 * both means a read younger than one poll is reused on re-entry and an older one is replaced
 * at once, and pinning them to a single constant keeps the two from drifting apart.
 */
export const CANDIDATES_POLL_INTERVAL_MS = 1000 * 30;

export const formatExperienceDates = (
  startDate: string,
  endDate: string | null | undefined,
  isCurrentEmployer: boolean,
): string => {
  const formattedStart = moment(startDate).format('MMMM DD, YYYY');

  if (isCurrentEmployer) {
    return `${formattedStart} / Present`;
  }

  if (endDate) {
    const formattedEnd = moment(endDate).format('MMMM DD, YYYY');
    return `${formattedStart} / ${formattedEnd}`;
  }

  return formattedStart;
};

export const formatGraduationDate = (graduationDate: string | null | undefined): string => {
  if (!graduationDate) {
    return '-';
  }

  return moment(graduationDate).format('MMMM DD, YYYY');
};

/**
 * Route an office lands on after closing the onsite-decline success popup.
 * Migrated (M2M) flow with other candidates still in play returns to the job;
 * everything else returns to the permanent-job overview.
 */
export const getDeclineSuccessRoute = ({
  isEnablePermanentJobM2M,
  hasMultipleCounters,
  jobId,
  postingId,
}: {
  isEnablePermanentJobM2M: boolean;
  hasMultipleCounters: boolean;
  jobId: string | undefined;
  postingId: string | undefined;
}): string =>
  isEnablePermanentJobM2M && hasMultipleCounters
    ? `/dashboard/job/${jobId}`
    : `/permanent-jobs/job-overview/${postingId}`;

interface ICandidatePositionCounter {
  candidate?: { id?: string } | null;
}

export interface ICandidatePosition {
  candidateNumber?: number;
  candidateCount: number;
  /** Undefined together with `candidateNumber` — an unlocated candidate has no page. */
  candidatePage?: number;
  /** Rows rendered on `candidatePage`; smaller than `limit` on a partial last page. */
  visibleCandidateCount?: number;
}

/**
 * Position (1-indexed) and total of a candidate within the counter-offer list,
 * plus which page of the list that position falls on.
 *
 * Shared by every confirm_candidate event so `candidate_number` /
 * `candidate_count` are derived identically on the impression
 * (`candidate_viewed`) and on the confirm/decline actions — otherwise the two
 * can't be joined per candidate (GOTU-4816). Returns an undefined
 * `candidateNumber` when the candidate isn't in the list, so a missing position
 * is absent from the payload rather than reported as a bogus 0.
 *
 * `candidateNumber` is absolute across pages, so on its own it can't answer
 * whether the office ever saw the candidate without paging — position 6 at the
 * default limit of 5 was never on the first screen. `candidatePage` /
 * `visibleCandidateCount` supply that (GOTU-4788).
 */
export const getCandidatePosition = (
  allCandidates: ICandidatePositionCounter[] | null | undefined,
  candidateId: string | null | undefined,
  limit: number | null | undefined = defaultFilters.limit,
): ICandidatePosition => {
  const candidateCount = allCandidates?.length ?? 0;
  // Mirrors getCandidatePage's `filters?.limit || defaultFilters.limit`, and additionally
  // rejects a negative limit — which is truthy and would yield a negative page.
  const effectiveLimit = limit && limit > 0 ? limit : defaultFilters.limit;

  // Bail on a missing id rather than matching it: a counter with no candidate would
  // otherwise satisfy `undefined === undefined` and report a bogus position.
  if (!candidateId) {
    return { candidateNumber: undefined, candidateCount };
  }

  const index =
    allCandidates?.findIndex((counter) => counter?.candidate?.id === candidateId) ?? -1;

  if (index < 0) {
    return { candidateNumber: undefined, candidateCount };
  }

  const candidatePage = Math.ceil((index + 1) / effectiveLimit);

  return {
    candidateNumber: index + 1,
    candidateCount,
    candidatePage,
    visibleCandidateCount: Math.min(
      effectiveLimit,
      candidateCount - (candidatePage - 1) * effectiveLimit,
    ),
  };
};

export type TConfirmScreenStatus =
  | 'another_job_popup'
  | 'ready'
  | 'redirect_scheduled'
  | 'redirect_empty'
  | 'decline_success'
  | 'loading';

interface IConfirmScreenStateInput {
  isAnotherJobPopupOpen: boolean;
  isConfirmWorkflowReady: boolean;
  isConfirmJobLoading: boolean;
  hasRenderedConfirmWorkflow: boolean;
  /** A confirm made on this screen succeeded — set from its outcome, whatever the outcome. */
  hasConfirmedCandidate: boolean;
  job: { id?: string; status?: string } | null | undefined;
  jobId: string | undefined;
  isSuccessFetchJobCandidates: boolean;
  hasCandidatesAvailable: boolean;
  isOnsiteInterview: boolean;
  isDeclineCandidateSuccess: boolean;
}

/**
 * Resolves the Confirm Candidate screen into a single named outcome so the
 * component can render/redirect by switching on one value instead of re-deriving
 * scattered booleans. Pure: it decides *what* the screen is, never performs the
 * navigation — callers act on the result (intentionally still during render; see
 * the redirect call sites).
 *
 * The `redirect_scheduled` outcome is deliberately suppressed mid-confirm and
 * after the workflow has rendered (GOTU-4020 / GOTU-4025 / GOTU-4027): a confirm
 * invalidates the job query, which refetches the job as SCHEDULED. Without these
 * guards the cold-entry redirect would fire post-confirm and bounce the office to
 * the shift summary before the invite popup shows, clobbering popup-driven
 * navigation. Only a true cold entry (stale SCHEDULED URL) should redirect.
 * TODO(GOTU-4025): remove the guards once confirm navigation is owned by the call
 * site instead of inferred from refetched server state.
 *
 * `redirect_empty` needs a narrower guard now that the candidates query polls
 * (GOTU-8310): a poll landing after a confirm has emptied the list, but before the job
 * query reports SCHEDULED, would otherwise read as "open job, nobody applied" and bounce
 * the office to the dashboard from under the confirm outcome. So it holds while a
 * confirm is in flight and once one has succeeded — and only then. Keying it on
 * `hasRenderedConfirmWorkflow` instead would also swallow an empty list nobody here
 * caused (a professional withdrawing while the office watches), and the screen would
 * spin forever: `isConfirmWorkflowReady` needs a candidate, so nothing else resolves it.
 */
export const getConfirmScreenStatus = ({
  isAnotherJobPopupOpen,
  isConfirmWorkflowReady,
  isConfirmJobLoading,
  hasRenderedConfirmWorkflow,
  hasConfirmedCandidate,
  job,
  jobId,
  isSuccessFetchJobCandidates,
  hasCandidatesAvailable,
  isOnsiteInterview,
  isDeclineCandidateSuccess,
}: IConfirmScreenStateInput): TConfirmScreenStatus => {
  if (isAnotherJobPopupOpen) {
    return 'another_job_popup';
  }

  if (isConfirmWorkflowReady) {
    return 'ready';
  }

  if (
    !isConfirmJobLoading &&
    !hasRenderedConfirmWorkflow &&
    jobId === job?.id &&
    job?.status === FilterStatusEnum.SCHEDULED
  ) {
    return 'redirect_scheduled';
  }

  if (
    job &&
    job.status === FilterStatusEnum.OPEN &&
    !hasConfirmedCandidate &&
    !isConfirmJobLoading &&
    isSuccessFetchJobCandidates &&
    !hasCandidatesAvailable &&
    !isOnsiteInterview
  ) {
    return 'redirect_empty';
  }

  if (isDeclineCandidateSuccess && isOnsiteInterview) {
    return 'decline_success';
  }

  return 'loading';
};
